import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Search, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getPublicNotices } from '../api';
import './PublicNotices.css';


const formatDate = (dateString) => new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
});


function PublicNotices() {
    const [notices, setNotices] = useState([]);
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        getPublicNotices()
            .then(setNotices)
            .catch(() => setError('Public notices are temporarily unavailable.'))
            .finally(() => setIsLoading(false));
    }, []);

    const filteredNotices = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        if (!normalized) return notices;
        return notices.filter((notice) => [
            notice.id,
            notice.public_title,
            notice.public_summary,
            notice.phone_model,
            notice.system_version,
            notice.software
        ].some((value) => value && value.toLowerCase().includes(normalized)));
    }, [notices, query]);

    return (
        <div className="notices-page">
            <header className="notices-header">
                <span className="notices-eyebrow"><ShieldCheck size={16} /> Public disclosure</span>
                <h1>Security notice board</h1>
                <p>Administrator-approved security disclosures from reported tickets.</p>
            </header>

            <div className="notices-toolbar">
                <label className="notices-search">
                    <Search size={17} aria-hidden="true" />
                    <span className="sr-only">Search public notices</span>
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search by ticket, device, app, or keyword"
                    />
                </label>
                <span className="notices-count">{filteredNotices.length} public ticket{filteredNotices.length === 1 ? '' : 's'}</span>
            </div>

            {isLoading && <div className="notices-state">Loading public notices...</div>}
            {error && <div className="notices-state notices-error">{error}</div>}
            {!isLoading && !error && filteredNotices.length === 0 && (
                <div className="notices-state">
                    <ShieldCheck size={24} />
                    <strong>{query ? 'No matching notices' : 'No public notices yet'}</strong>
                    <span>{query ? 'Try a different search term.' : 'Approved ticket summaries will appear here.'}</span>
                </div>
            )}

            {!isLoading && !error && filteredNotices.length > 0 && (
                <div className="notices-list">
                    {filteredNotices.map((notice) => (
                        <article className="notice-record" key={notice.id}>
                            <div className="notice-record-rail">
                                <span>SC-{notice.id.slice(0, 8).toUpperCase()}</span>
                                <time dateTime={notice.published_at}>{formatDate(notice.published_at)}</time>
                            </div>
                            <div className="notice-record-body">
                                <h2><Link to={`/notices/${notice.id}`}>{notice.public_title}</Link></h2>
                                <p>{notice.public_summary}</p>
                                <div className="notice-record-meta">
                                    <span>{notice.phone_model}</span>
                                    <span>{notice.system_version}</span>
                                    <span>{notice.software}</span>
                                </div>
                            </div>
                            <Link to={`/notices/${notice.id}`} className="notice-record-link" aria-label={`Read ${notice.public_title}`}>
                                <ArrowRight size={18} />
                            </Link>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

export default PublicNotices;
